<template>
    <a-layout-header
        :style="{
            display: 'flex',
            justifyContent: 'space-between'
        }"
        class="l-header"
        data-cy="layout-navbar"
    >
        <div class="mobile-menu">
            <menu-outlined v-if="!isMenuShowing" @click="setMenuShowing(true)" />
            <close-outlined v-else @click="setMenuShowing(false)" />
        </div>
        <router-link v-if="logo" to="/" class="bp-logo">
            <img
                :src="logo"
                alt="BP logo"
                :style="{
                    maxHeight: '90%',
                    maxWidth: '100px'
                }"
                
            />
        </router-link>

        <span v-else />

        <img src="@/assets/images/logo-color-dark.svg" alt="Color logo" class="color-logo color-logo-mobile" v-if="isMobile" />


        <div :style="{ display: 'flex' }">
            <div :class="{ hidden: isSharedLink }" id="navbar-target"></div>

            <span :class="{ hidden: isSharedLink || isMcpUser }">
                <notifications></notifications>
            </span>

            <a-menu
                :class="{ hidden: isSharedLink }"
                mode="horizontal"
                class="l-header-menu"
                :style="{ lineHeight: '52px' }"
                :selected-keys="[]"
            >
                <a-sub-menu>
                    <template #title>
                        <a-avatar
                            :style="{
                                backgroundColor: '#4fa1e7'
                            }"
                            data-cy="team-single-item-info-avatar"
                        >
                            <loading-outlined v-if="isLoggedUserEmpty" spin data-cy="layout-navbar-avatar-spinner" />
                            <template v-else>{{ initials }}</template>
                        </a-avatar>
                    </template>
                    <a-menu-item v-for="item in menuItems" :key="item.path" :style="menuItemStyles(item)">
                        <router-link v-if="item.path" :to="item.path" @click="onMenuItemClick(item)">
                            <span>{{ item.text }}</span>
                        </router-link>
                        <span v-else class="actionable-nav-item" @click="item.onClick">
                            {{ item.text }}
                        </span>
                    </a-menu-item>
                </a-sub-menu>
            </a-menu>

            <a
                v-if="!isMobile"
                href="https://affogata.com"
                target="_blank"
                rel="noopener"
                class="color-logo-link"
            >
                <img src="@/assets/images/logo-color.svg" alt="Color logo" class="color-logo" />
            </a>
        </div>
    </a-layout-header>
</template>

<script>
import axios from 'axios';
import { mapGetters, mapActions } from 'vuex';
import { url } from '@/config';
import makeInitials from '@/helpers/makeInitials';
import { MenuOutlined, CloseOutlined, LoadingOutlined } from '@ant-design/icons-vue';
import Notifications from './notifications/Index';
import getSharedLinkCode from '@/helpers/dashboard/getSharedLinkCode';
import { suppressToasterErrors } from '@/helpers/toasterSuppression';
import { ACTION_TYPE_PAGE_VIEW, EVENT_TYPE_CLICK, ACTION_MODULE_NEW_UI_NAVIGATION_CHANGE } from '@/assets/common/account-events';

const ETORO_BP_ID = 11;

export default {
    components: {
        MenuOutlined,
        CloseOutlined,
        LoadingOutlined,
        Notifications
    },

    data() {
        return {
            menuItems: []
        };
    },

    async created() {
        this.menuItems = await this.getMenuItems();
    },

    watch: {
        async loggedUser() {
            this.menuItems = await this.getMenuItems();
        }
    },

    computed: {
        ...mapGetters({
            isAdmin: 'auth/isAdmin',
            isGuest: 'auth/isGuest',
            isMcpUser: 'auth/isMcpUser',
            isMobile: 'isMobile/isMobile',
            globalLoading: 'loading',
            loggedUser: 'auth/loggedUser',
            isSysAdmin: 'auth/isSysAdmin',
            isMenuShowing: 'menuShowing',
            isProd: 'environments/isProd',
            isPreprod: 'environments/isProd',
            isProdOrPreprod: 'environments/isProdOrPreprod',
            investigatingAnotherAccount: 'auth/isAccountOvertaken'
        }),

        isLoggedUserEmpty() {
            return !Object.keys(this.loggedUser ?? {}).length;
        },

        initials() {
            const { firstName = '', lastName = '' } = this.loggedUser ?? {};

            return makeInitials(`${firstName} ${lastName}`.trim());
        },

        logo() {
            return this.loggedUser?.businessProfile?.logoUrl;
        },

        isSharedLink() {
            return !!getSharedLinkCode();
        }
    },

    methods: {
        ...mapActions({
            exitTakeOver: 'auth/exitTakeOver',
            setIsSpinnerModalOpened: 'modals/setIsSpinnerModalOpened',
            setMenuShowing: 'setMenuShowing',
            can: 'auth/can',
            storeAccountEvent: 'accountEvent/store'
        }),

        async exitTakeOverMode() {
            this.setIsSpinnerModalOpened(true);

            try {
                await this.exitTakeOver();

                location.reload();
            } catch (error) {
                console.error(error);

                this.setIsSpinnerModalOpened(false);

                this.$toasterError();
            }
        },

        menuItemStyles(item) {
            const styles = {};

            const borderStyle = '1px solid #e8e8e8';

            if (item.dividerBottom) {
                styles.borderBottom = borderStyle;
            }

            if (item.dividerTop) {
                styles.borderTop = borderStyle;
            }

            return styles;
        },

        brakeAllRequests() {
            window.stop();
        },

        onMenuItemClick(item) {
            this.brakeAllRequests();

            if (item.path === '/logout') {
                suppressToasterErrors(5000);
                axios.cancelAllRequests();
            }
        },

        async getMenuItems() {
            const moderationReportItem = {
                path: '/moderation-report',
                text: 'Moderation Report'
            };

            const settingsItem = {
                path: '/account/settings/preferences',
                text: 'Settings',
                dividerBottom: true
            };

            const logoutItem = {
                path: '/logout',
                text: 'Logout'
            };

            const exitTakeOverItem = {
                onClick: this.exitTakeOverMode,
                text: 'Restore Admin Account'
            };

            const adminPagesItem = {
                path: '/admin',
                text: 'Admin BP'
            };

            const topicPageItem = {
                path: '/topics',
                text: 'Custom Topics'
            };

            // const peoplePageItem = {
            //     path: '/settings/users',
            //     text: 'People'
            // };

            const oldDashboardTeam = {
                onClick: () => (window.location.href = `${url}/settings/teams`),
                text: 'Teams'
            };

            const oldPeoplePage = {
                onClick: () => (window.location.href = `${url}/settings/people`),
                text: 'People'
            };

            const oldProfilesPage = {
                onClick: () => (window.location.href = `${url}/settings/profiles`),
                text: 'Profiles'
            };

            const oldCustomTopicsTaxonomyPage = {
                onClick: async () => {
                    await this.storeAccountEvent({
                        action_type: ACTION_TYPE_PAGE_VIEW,
                        event_type: EVENT_TYPE_CLICK,
                        action_module: ACTION_MODULE_NEW_UI_NAVIGATION_CHANGE
                    });

                    window.location.href = `${url}/n/topics`
                },
                text: 'Custom Topics',
                dividerBottom: true,
                dividerTop: true
            };

            const oldRulesPage = {
                onClick: () => (window.location.href = `${url}/rules`),
                text: 'Rules'
            };

            const oldReportsPage = {
                onClick: () => (window.location.href = `${url}/reports`),
                text: 'Reports'
            };

            const SettingsPage = {
                path: '/account/settings',
                text: 'Settings',
                dividerBottom: true
            };

            // const dashboardTeamsPage = {
            //     path: '/settings/teams',
            //     text: 'Teams'
            // };

            const oldDashboard = {
                onClick: () => (window.location.href = `${url}/dashboard`),
                text: 'Dashboard V1'
            };

            const oldDashboardTemplates = {
                onClick: () => (window.location.href = `${url}/dashboard/?showModal=templates`),
                text: 'Templates'
            };

            let items;

            if (this.isMcpUser) {
                items = [logoutItem];

                if (this.investigatingAnotherAccount) {
                    items.push(exitTakeOverItem);
                }
            } else if (this.isProdOrPreprod) {
                items = [];

                if (this.isSysAdmin) {
                    items.push(adminPagesItem);
                }

                if (await this.can({ scope: 'settings', permission: 'index', action: 'write' })) {
                    items.push(oldPeoplePage);
                    items.push(oldProfilesPage);
                    items.push(oldDashboardTeam);
                }

                items.push(oldCustomTopicsTaxonomyPage);

                if (this.investigatingAnotherAccount) {
                    items.push(exitTakeOverItem);
                }

                if (this.isAdmin || this.isSysAdmin) {
                    items.push(oldRulesPage);
                }

                items.push(oldDashboardTemplates);

                if (this.loggedUser?.businessProfile?.id === ETORO_BP_ID) {
                    items.push(oldDashboard);
                }

                items.push(SettingsPage);

                if (this.isAdmin || this.isSysAdmin) {
                    items.push(oldReportsPage);
                }

                if (this.loggedUser?.businessProfile?.id === ETORO_BP_ID || this.isSysAdmin) {
                    items.push(moderationReportItem);
                }

                items.push(logoutItem);
            } else if (this.isGuest) {
                items = [settingsItem, logoutItem];

                if (this.investigatingAnotherAccount) {
                    items.unshift(exitTakeOverItem);
                }
            } else {
                items = [
                    {
                        path: '/settings/users',
                        text: 'Users'
                    },
                    {
                        path: '/settings/channels',
                        text: 'Channels'
                    },
                    {
                        onClick: () => (window.location.href = `${url}/settings/teams`),
                        text: 'Teams'
                    },
                    oldDashboard,
                    settingsItem,
                    moderationReportItem,
                    topicPageItem
                ];

                if (this.investigatingAnotherAccount) {
                    items.push(exitTakeOverItem);
                }

                if (this.isSysAdmin) {
                    items.push(adminPagesItem);
                }

                items.push(logoutItem);
            }

            return items;
        }
    }
};
</script>

<style lang="scss">
@import "@/assets/styles/_mixins.scss";


.patch-note {
    color: #7c8791;
    font-size: 16px;
}


.l-header {
    box-shadow: 0 1px 0 0 rgba(0, 0, 0, 0.25);
    // Above the fixed side panels (Helper is 10, the tablet rail is 11) so the
    // top bar and its underline are never painted over.
    z-index: 12;
    // background: isMobile ? '#153E50': '#fff',

    background: #fff !important;

    // antd paints 64px by default. Its rule is `.ant-layout .ant-layout-header`
    // (plus a cssinjs hash class), so a bare `.l-header` cannot outrank it and
    // the bar stayed 64px while everything anchored to $header-height sat 12px
    // too high — the Helper panel ended up over the menu.
    height: $header-height !important;
    line-height: $header-height !important;

    .mobile-menu {
        color: #fff;
        display: none;
    }

    .color-logo-mobile {
        display: none;
    }

    .color-logo-link {
        display: inline-flex;
        align-items: center;
        cursor: pointer;
    }

    @include tablet {
        background: #153E50 !important;
        .bp-logo {
            display: none;
        }

        .color-logo-mobile {
            display: block;
        }

        .mobile-menu {
            display: block;
        }

        .l-header-menu {
            background: transparent !important;
        }
        

        .l-header-menu li {
            padding: 0 8px !important;
            
        }

        padding: 0 16px !important;
    }

    .hidden {
        display: none;
    }
}
</style>
